import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { useDispatch } from "react-redux";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFolder, faFile, faFileLines, faTableCells } from "@fortawesome/free-solid-svg-icons";
import { openWindow } from "../../Slices/windowsSlice";
import { setClipboard } from "../webtopUiSlice";
import WebtopLayoutContext from "../layout/WebtopLayoutContext";
import ContextMenu from "../desktop/ContextMenu";
import ConfirmDialog from "../desktop/ConfirmDialog";

const DRAG_THRESHOLD = 5;
const DOUBLE_TAP_MS = 400;
const isSheetNode = (n) => (n.name || "").toLowerCase().endsWith(".sheet");
const isTextNode = (n) =>
  !isSheetNode(n) &&
  (n.type === "textfile" ||
    (n.mimeType || "").startsWith("text/") ||
    (n.name || "").toLowerCase().endsWith(".txt"));

/**
 * DesktopFiles — the contents of the user's "Desktop" folder as movable icons on
 * the webtop desktop (Windows model). Double-click opens (text→Notepad, folder→
 * This PC, file→preview); icons are draggable (positions persist in the layout);
 * right-click gives Open / Rename / Copy / Cut / Delete. filesApi is lazy.
 */
const DesktopFiles = () => {
  const dispatch = useDispatch();
  const { layout, updateLayout } = useContext(WebtopLayoutContext);
  const positions = (layout && layout.desktopFilePositions) || {};

  const [files, setFiles] = useState([]);
  const [ctx, setCtx] = useState(null);
  const [renamingId, setRenamingId] = useState(null);
  const [renameValue, setRenameValue] = useState("");
  const [drag, setDrag] = useState(null); // { id, x, y }
  const lastTapRef = useRef({ id: null, time: 0 }); // manual double-click detection
  const [confirmNode, setConfirmNode] = useState(null); // pending delete confirmation
  const [notice, setNotice] = useState(null); // surfaced error

  const load = useCallback(async () => {
    try {
      const { getDesktopFolder, listFiles } = await import("./filesApi");
      const folder = await getDesktopFolder();
      if (!folder?.id) return;
      setFiles(await listFiles("personal", folder.id));
    } catch (e) {
      /* best-effort */
    }
  }, []);

  useEffect(() => {
    load();
    const onFocus = () => load();
    window.addEventListener("focus", onFocus);
    // Refresh immediately when a file is created/saved/deleted anywhere (e.g.
    // saving into Desktop from Notepad's Save-as) instead of waiting for focus.
    window.addEventListener("webtop:files-changed", onFocus);
    return () => {
      window.removeEventListener("focus", onFocus);
      window.removeEventListener("webtop:files-changed", onFocus);
    };
  }, [load]);

  const defaultPos = (index) => ({
    x: Math.max(0, (typeof window !== "undefined" ? window.innerWidth : 1200) - 120),
    y: 20 + index * 100,
  });
  const posFor = (file, index) =>
    (drag && drag.id === file.id ? drag : positions[file.id]) || defaultPos(index);

  const open = (node) => {
    const sc = node.scope || "personal";
    if (isSheetNode(node)) {
      dispatch(openWindow({ path: `/webtop-sheets?fileId=${node.id}`, title: node.name, deduplicate: true }));
    } else if (isTextNode(node)) {
      dispatch(openWindow({ path: `/webtop-notepad?fileId=${node.id}`, title: node.name, deduplicate: true }));
    } else if (node.type === "folder") {
      dispatch(openWindow({
        path: `/webtop-files?scope=${sc}&parentId=${node.id}&name=${encodeURIComponent(node.name)}`,
        title: node.name,
        deduplicate: true,
      }));
    } else {
      dispatch(openWindow({
        path: `/webtop-files?scope=${sc}&parentId=${node.parentId || ""}&name=Desktop&preview=${node.id}`,
        title: "This PC",
        deduplicate: true,
      }));
    }
  };

  // Drag to reposition (commits to layout.desktopFilePositions); a non-move
  // press does nothing — opening is via double-click.
  const startDrag = (e, file, base) => {
    if (e.button !== 0) return;
    const sx = e.clientX;
    const sy = e.clientY;
    let dragged = false;
    const onMove = (ev) => {
      const dx = ev.clientX - sx;
      const dy = ev.clientY - sy;
      if (!dragged && Math.abs(dx) + Math.abs(dy) > DRAG_THRESHOLD) dragged = true;
      if (dragged) setDrag({ id: file.id, x: Math.max(0, base.x + dx), y: Math.max(0, base.y + dy) });
    };
    const onUp = (ev) => {
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
      if (dragged) {
        const x = Math.max(0, base.x + (ev.clientX - sx));
        const y = Math.max(0, base.y + (ev.clientY - sy));
        updateLayout({ desktopFilePositions: { ...positions, [file.id]: { x, y } } });
        setDrag(null);
        return;
      }
      // Not a drag → manual double-click detection (the native onDoubleClick is
      // unreliable alongside this mousedown-based drag handler).
      const now = Date.now();
      const last = lastTapRef.current;
      if (last.id === file.id && now - last.time < DOUBLE_TAP_MS) {
        lastTapRef.current = { id: null, time: 0 };
        open(file); // double click → open
      } else {
        lastTapRef.current = { id: file.id, time: now };
      }
    };
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
  };

  const commitRename = async (id) => {
    const name = renameValue.trim();
    setRenamingId(null);
    if (!name) return;
    try {
      const { updateNode } = await import("./filesApi");
      await updateNode({ id, name });
      await load();
    } catch (e) {
      /* ignore */
    }
  };

  // Ask first (in-app dialog — the native window.confirm silently fails inside
  // the Electron webtop window, which made deletes appear to do nothing).
  const remove = (node) => {
    setCtx(null);
    setConfirmNode(node);
  };

  const doRemove = async (node) => {
    setConfirmNode(null);
    try {
      const { deleteNode } = await import("./filesApi");
      await deleteNode(node.id);
      await load();
    } catch (e) {
      // Surface the error instead of swallowing it (was: silent catch).
      setNotice(e?.message || "Couldn't delete this file. Please try again.");
    }
  };

  const copyToClipboard = (node, mode) => {
    setCtx(null);
    dispatch(setClipboard({ id: node.id, mode, scope: node.scope, name: node.name, type: node.type }));
  };

  const onContextMenu = (e, node) => {
    e.preventDefault();
    e.stopPropagation();
    setCtx({
      x: e.clientX,
      y: e.clientY,
      items: [
        { label: "Open", onClick: () => open(node) },
        { label: "Rename", onClick: () => { setRenamingId(node.id); setRenameValue(node.name); } },
        { label: "Copy", onClick: () => copyToClipboard(node, "copy") },
        { label: "Cut", onClick: () => copyToClipboard(node, "cut") },
        { separator: true },
        { label: "Delete", danger: true, onClick: () => remove(node) },
      ],
    });
  };

  // Note: don't early-return on an empty folder — the confirm dialog / error
  // notice must still render (e.g. after a failed delete, or while confirming).

  return (
    <div className="webtop-desktop-files">
      {files.map((f, i) => {
        const pos = posFor(f, i);
        return (
          <div
            key={f.id}
            className={`webtop-desktop-files__item${drag && drag.id === f.id ? " webtop-desktop-files__item--dragging" : ""}`}
            style={{ left: pos.x, top: pos.y }}
            role="button"
            tabIndex={0}
            onMouseDown={(e) => startDrag(e, f, pos)}
            onContextMenu={(e) => onContextMenu(e, f)}
            title={f.name}
          >
            <span className="webtop-desktop-files__glyph">
              <FontAwesomeIcon icon={f.type === "folder" ? faFolder : isSheetNode(f) ? faTableCells : isTextNode(f) ? faFileLines : faFile} />
            </span>
            {renamingId === f.id ? (
              <input
                className="webtop-desktop-files__rename"
                autoFocus
                value={renameValue}
                onChange={(e) => setRenameValue(e.target.value)}
                onBlur={() => commitRename(f.id)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") commitRename(f.id);
                  if (e.key === "Escape") setRenamingId(null);
                }}
                onMouseDown={(e) => e.stopPropagation()}
                onClick={(e) => e.stopPropagation()}
                onDoubleClick={(e) => e.stopPropagation()}
              />
            ) : (
              <span className="webtop-desktop-files__label">{f.name}</span>
            )}
          </div>
        );
      })}

      {ctx && <ContextMenu x={ctx.x} y={ctx.y} items={ctx.items} onClose={() => setCtx(null)} />}

      {confirmNode && (
        <ConfirmDialog
          title="Delete file"
          message={`Delete ${
            confirmNode.type === "folder" ? "this folder and everything in it" : `"${confirmNode.name}"`
          }? It moves to the Recycle Bin.`}
          confirmLabel="Delete"
          onConfirm={() => doRemove(confirmNode)}
          onCancel={() => setConfirmNode(null)}
        />
      )}

      {notice &&
        typeof document !== "undefined" &&
        document.body &&
        ReactDOM.createPortal(
          <div
            className="webtop-notice webtop-notice--error"
            role="alert"
            onClick={() => setNotice(null)}
            title="Dismiss"
          >
            {notice}
          </div>,
          document.body
        )}
    </div>
  );
};

export default DesktopFiles;
